import { useCallback, useEffect, useRef, useState } from 'react';
import { processAIQuery } from '../../services/oracleService.js';
import { useAppState, useAppActions } from '../../context/AppContext.jsx';

import OracleHeader from './OracleHeader.jsx';
import SuggestedPrompts from './SuggestedPrompts.jsx';
import ChatBubble from './ChatBubble.jsx';
import TypingIndicator from './TypingIndicator.jsx';
import ChatComposer from './ChatComposer.jsx';
import LocalAiSettings from './LocalAiSettings.jsx';

/**
 * OracleScreen — conversational interface to the F1 AI assistant.
 *
 * State model:
 *   messages    Array<UserMessage | AssistantMessage>
 *   isPending   true while an assistant response is in flight
 *   error       transient error from the last query (cleared on retry)
 *
 * Why local state, not global context:
 *   The conversation is per-session by design — users do not expect their
 *   chat history to survive a tab switch in the current product spec. If
 *   the product team wants persistent history later, lift this state into
 *   AppContext (or, more likely, IndexedDB) — the rest of the component
 *   stays the same.
 *
 * Auto-scroll:
 *   On every messages change OR while the typing indicator is visible, we
 *   scroll the bottom anchor into view. Smooth scroll respects
 *   `prefers-reduced-motion` because Tailwind defaults to it.
 *
 * Cross-screen seeding:
 *   Other screens can drop a prompt into the composer via
 *   `seedOraclePrompt()` on the AppContext (see NewsScreen). We consume
 *   the pending prompt on mount and pipe it into ChatComposer as a
 *   `seededValue` — the user can still edit before pressing Send.
 */

export default function OracleScreen() {
  const [messages, setMessages] = useState([]);
  const [isPending, setIsPending] = useState(false);
  const [seededValue, setSeededValue] = useState('');
  const [showAiSettings, setShowAiSettings] = useState(false);
  const bottomRef = useRef(null);

  // Mirror of `messages` for reading inside the async submit handler without
  // stale-closure surprises — used to hand prior turns to the LLM as context.
  const messagesRef = useRef([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const { pendingOraclePrompt } = useAppState();
  const { consumeOraclePrompt } = useAppActions();

  // Consume any cross-screen prompt seeding.
  useEffect(() => {
    if (pendingOraclePrompt) {
      setSeededValue(pendingOraclePrompt);
      consumeOraclePrompt();
    }
  }, [pendingOraclePrompt, consumeOraclePrompt]);

  // Auto-scroll on message change or typing.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, isPending]);

  const handleSubmit = useCallback(async (text) => {
    // Prior turns become the LLM's context; snapshot before we append this one.
    const history = messagesRef.current;

    const userMsg = {
      id: `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      role: 'user',
      text,
      timestamp: new Date().toISOString(),
    };
    setMessages((m) => [...m, userMsg]);
    setIsPending(true);
    setSeededValue('');

    // Placeholder id for the assistant bubble, in case the answer streams in.
    const assistantId = `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let streamed = false;

    // Called with the running text as LLM deltas arrive — grows a live bubble
    // and hides the typing indicator once the first token lands. Deterministic
    // answers (facts, KB) never call this, so they append as a single message.
    const onStream = (partialText) => {
      streamed = true;
      setIsPending(false);
      setMessages((m) => {
        const placeholder = {
          id: assistantId,
          role: 'assistant',
          tone: 'conversational',
          streaming: true,
          blocks: [{ type: 'paragraph', text: partialText }],
        };
        return m.some((x) => x.id === assistantId)
          ? m.map((x) => (x.id === assistantId ? placeholder : x))
          : [...m, placeholder];
      });
    };

    try {
      const reply = await processAIQuery(text, { onStream, history });
      // If it streamed, swap the live placeholder for the final formatted reply;
      // otherwise append the reply normally.
      setMessages((m) =>
        streamed && m.some((x) => x.id === assistantId)
          ? m.map((x) => (x.id === assistantId ? { ...reply, id: assistantId } : x))
          : [...m, reply]
      );
    } catch (err) {
      setMessages((m) => [
        ...m.filter((x) => x.id !== assistantId),
        {
          id: `e-${Date.now()}`,
          role: 'assistant',
          tone: 'conversational',
          blocks: [
            {
              type: 'callout',
              text: "Sorry — the Oracle didn't respond in time. Please try again.",
            },
          ],
        },
      ]);
      if (typeof console !== 'undefined') console.error('Oracle query failed', err);
    } finally {
      setIsPending(false);
    }
  }, []);

  const handleClear = () => {
    if (isPending) return;
    setMessages([]);
  };

  const hasConversation = messages.length > 0 || isPending;

  return (
    <div className="flex min-h-full flex-col px-5">
      <OracleHeader
        hasMessages={messages.length > 0}
        onClear={handleClear}
        onOpenAiSettings={() => setShowAiSettings((value) => !value)}
      />

      {showAiSettings ? <LocalAiSettings onClose={() => setShowAiSettings(false)} /> : null}

      <div className="flex flex-1 flex-col">
        {!hasConversation ? (
          <SuggestedPrompts onPromptSelected={(p) => handleSubmit(p)} />
        ) : (
          <div className="pb-4">
            {messages.map((msg) => (
              <ChatBubble key={msg.id} message={msg} />
            ))}
            {isPending ? <TypingIndicator /> : null}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      <ChatComposer onSubmit={handleSubmit} isPending={isPending} seededValue={seededValue} />
    </div>
  );
}
